import type {PagePromptContent} from '../store/thread';
import {
  type AgentPageContextReader,
  reduceAgentPageContextLayers,
} from './channel';
import {reportReducer} from './views/report';
import {sweepReducer} from './views/sweep';
import {workspaceReducer} from './views/workspace';

/**
 * collectAgentPageContext returns the page-content objects of all mounted
 * layers, ordered from the outermost layer to the innermost.
 */
export function collectAgentPageContext(): PagePromptContent[] {
  return reduceAgentPageContextLayers((type, read) => {
    const reduce = reducers[type as PagePromptContent['type']];

    // Drop a layer with no field carrying data, so a mounted but empty layer
    // emits nothing rather than a bare {type} marker.
    const content = reduce?.(read);
    if (content == null || !Object.values(content).some(hasData)) {
      return null;
    }

    return {type, ...content} as PagePromptContent;
  });
}

/**
 * ViewReducer assembles one page-content type from the channels in its layer.
 */
type ViewReducer<K extends PagePromptContent['type']> = (
  read: AgentPageContextReader
) => Omit<Extract<PagePromptContent, {type: K}>, 'type'> | null;

/**
 * reducers maps each page-content type to its view's reducer. New page types
 * register their reducer here and define their channels under views/.
 */
const reducers: {[K in PagePromptContent['type']]: ViewReducer<K>} = {
  workspace: workspaceReducer,
  report: reportReducer,
  sweep: sweepReducer,
};

/**
 * hasData treats undefined and empty arrays as "no data", so a layer with
 * nothing to report is dropped rather than emitted.
 */
function hasData(value: unknown): boolean {
  return value !== undefined && !(Array.isArray(value) && value.length === 0);
}
